import {
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    ToggleButtonGroup,
    Tooltip,
    useTheme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import _, { get, isEmpty } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import clsx from 'clsx';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Grid from '@mui/material/Grid';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import Divider from '@mui/material/Divider';
import ExploreOffIcon from '@mui/icons-material/ExploreOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import Popover from '@mui/material/Popover';
import ToggleButton from '@mui/material/ToggleButton';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { TimerCountUp, TimerCountUpSimple } from '../../controls/agyle/timer';
import CheckIcon from '@mui/icons-material/Check';
import config from '../../app/config';
import InformationBox from '../other/informationBox';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import PropTypes from 'prop-types';
import SettingsIcon from '@mui/icons-material/Settings';
import { useDispatch, useSelector } from 'react-redux';
import { agylePref, saveAgylePreference } from '../../store/reducers/preferences';
import useOpenState from '../../hooks/useOpenState';
import PriorityOptions from '../../pages/utils/priorityOptions';
import Dialog from '@mui/material/Dialog';
import { setSnackbar } from '../../store/reducers/globals';
import { riskLevels } from '../../pages/ward/forms/observation/obsUtils';
import { CopyIcon } from '../icons/adminIcons';
import { getTableScrollbar } from '../../util/scrollbar';
import { referredToService } from '../../store/reducers/dicts';
import { UtilsMomentJS } from '../../store/utilsMoment';
import { getReferralsColor } from '../../controls/agyle/tasks/taskUtil';
import { formNameException } from '../notes/notesUtil';
import AgyleCheckBox from '../ui/checkbox/agyleCheckBox';
import useDict from '../../hooks/useDict';
import useWardDict from '../../hooks/useWardDict';
import AgyleButton from '../ui/buttons/agyleButton';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Pagination from '@mui/material/Pagination';
import AgyleDatePicker from '../ui/fields/agyleDatePicker';
import { calculateArrivalDate } from '../../util/patientUtil';
import ClearIcon from '@mui/icons-material/Clear';
import AgyleSelectField from '../ui/selector/agyleSelectField';
import Badge from '@mui/material/Badge';

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        '&.maxHeight': {
            maxHeight: 'calc(100vh - 300px)',
        },
        '&.refresh': {
            marginTop: 5,
            borderCollapse: 'separate',
            borderSpacing: '0px 4px',
            paddingRight: 5,
        },
    },
    headCell: {
        // fontSize: "13px",
        padding: 2,
        background: theme.palette.background.default,
        whiteSpace: 'pre',
        '&.refresh': {
            fontSize: 14,
            background: theme.palette.background.paper,
            paddingLeft: 5,
        },
        '&:first-child': {
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
        },
        '&:last-child': {
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
            paddingRight: 5,
        },
    },
    refreshCol: {
        fontSize: 'small',
        backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#313233',
        textWrapMode: 'nowrap',
        paddingRight: '8px !important',
        '&:first-child': {
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
            padding: 0,
            paddingLeft: 5,
        },
        '&:last-child': {
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
            paddingRight: 5,
        },
    },
    row: {
        '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? '#ccc' : '#898888',
        },
    },
    tableRow: {
        fontSize: 'small',
        '&:hover': {
            background: theme.palette.mode === 'light' ? '#414141' : '#b1abab',
        },
        '&.odd': {
            background: theme.palette.mode === 'dark' ? '#666666' : '#ccc',
            '&:hover': {
                background: theme.palette.mode === 'dark' ? '#414141' : '#ccc',
            },
        },
    },
    input: {
        fontSize: '15px',
        margin: 8,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '6%',
    },
    cancelledObs: {
        fontWeight: 'bold',
        color: theme.palette.error.main,
    },
    suspendedObs: {
        fontWeight: 'bold',
        color: theme.palette.warning.main,
    },
    scoreColor: {
        display: 'inline-block',
        width: '30px',
        // marginLeft: "13px",
        textAlign: 'center',
        padding: 4,
        borderRadius: 6,
    },
    obsColorLow: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
    },
    obsColorMediumLow: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
    },
    obsColorMedium: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
    },
    obsColorHigh: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
    },
    obsColorUnknown: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
    },
    timer: {
        borderRadius: 5,
        display: 'inline-block',
        overflow: 'hidden',
        minWidth: 60,
        textAlign: 'center',
        '& .timer-count-up': {
            fontSize: 14,
            height: 20,
            // width: "max-content",
        },
    },
    centerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    bedStatus: {
        borderRadius: 10,
        padding: 5,
        textAlign: 'center',
        '&.Ready': {
            backgroundColor: theme.palette.acuityColors.color4.background,
            color: theme.palette.acuityColors.color4.text,
        },
        '&.Requested': {
            backgroundColor: theme.palette.acuityColors.color1.background,
            color: theme.palette.acuityColors.color1.text,
        },
        '&.Allocated': {
            backgroundColor: theme.palette.acuityColors.color3.background,
            color: theme.palette.acuityColors.color3.text,
        },
        '&.Canceled': {
            backgroundColor: theme.palette.acuityColors.color5.background,
            color: theme.palette.acuityColors.color5.text,
        },
    },
    siteStatus: {
        borderRadius: 10,
        padding: 5,
        textAlign: 'center',
        '&.Offsite': {
            backgroundColor: '#EEEEE4',
            color: '#000000',
        },
        '&.Bournemouth': {
            backgroundColor: '#2596BE',
            color: theme.palette.acuityColors.color1.text,
        },
        '&.Poole': {
            backgroundColor: '#BE4D25',
            color: theme.palette.acuityColors.color3.text,
        },
    },
    backgroundButton: {
        borderRadius: 10,
        padding: 5,
        textAlign: 'center',
    },
    conclusionStatus: {
        borderRadius: 10,
        padding: 5,
        textAlign: 'center',
        '&.ready_to_discharge': {
            backgroundColor: '#FFFFFF',
            color: '#000000',
        },
        '&.deceased': {
            backgroundColor: '#000000',
            color: '#FFFFFF',
        },
        '&.ready_to_transfer': {
            backgroundColor: theme.palette.acuityColors.color3.background,
            color: theme.palette.acuityColors.color3.text,
        },
        '&.ready_to_admit_to_ward': {
            backgroundColor: theme.palette.acuityColors.color5.background,
            color: theme.palette.acuityColors.color5.text,
        },
    },
    stickyCol: {
        position: 'sticky',
        left: 0,
        zIndex: 30,
    },
    referral: {
        display: 'inline-block',
        width: 40,
        margin: 1,
        height: 30,
        textAlign: 'center',
        lineHeight: '30px',
        color: '#fff',
        borderRadius: 6,
        fontSize: 'small',
        marginRight: 2,
        '&.completed': {
            border: '2px solid #000',
            color: '#000',
            lineHeight: '25px',
        },
    },
    escalationLevel: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '70px',
        borderRadius: 5,
        height: '25px',
        lineHeight: '14px',
        fontWeight: 700,
        border: '1px solid transparent',
        paddingTop: 1,
        fontSize: '12px',
        marginLeft: 'auto',
        marginRight: 'auto',
        '&.core': {
            borderColor: '#4EAC6F',
            color: '#4EAC6F',
        },
        '&.planned': {
            borderColor: '#F7A202',
            color: '#F7A202',
        },
        '&.unplanned': {
            borderColor: '#E25B61',
            color: '#E25B61',
        },
    },
    cleaningLevel: {
        width: 30,
        height: 30,
        display: 'flex',
        paddingTop: 1,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 8,
        fontWeight: 700,
        fontSize: '0.8rem',
        color: '#ffffff',
        '&.level1': {
            backgroundColor: '#4EAC6F',
        },
        '&.level2': {
            backgroundColor: '#F7A202',
        },
        '&.level3': {
            backgroundColor: '#E25B61',
        },
    },
    status: {
        padding: '4px 6px',
        minWidth: 75,
        height: 30,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 6,
        fontWeight: 700,
        color: '#ffffff',
        fontSize: 12,
        '&.allocated': {
            backgroundColor: '#054595',
        },
        '&.cancelled, &.canceled, &.unused': {
            backgroundColor: '#e25b61',
        },
        '&.completed, &.ready, &.open': {
            backgroundColor: '#4eac6f',
        },
        '&.requested': {
            backgroundColor: '#e25b61',
        },
        '&.closed, &.pre-reg, &.nr': {
            backgroundColor: '#494B4C',
            color: '#ACAFB3',
        },
        '&.cleaning, &.occupied, &.blocked': {
            backgroundColor: '#F7A202',
        },
        '&.available': {
            backgroundColor: '#4EAC6F',
        },
        '&.siderooms': {
            border: `1px solid ${theme.palette.mode === 'light' ? '#000' : '#ffffff'}`,
            minWidth: 100,
            fontWeight: 500,
        },
        '&.home, &.reserved, &.disabled': {
            backgroundColor: '#ACAFB3',
            color: '#313233',
        },
    },
    strikeThrough: {
        textDecoration: 'line-through',
    },
    highlightRow: {
        backgroundColor: '#4EAC6F',
    },
}));

export function createAgyleTableColumn(
    id,
    description,
    order,
    field,
    type,
    headerType,
    size,
    align,
    disablePadding,
    enableFilter,
    width,
    minWidth,
    option = [],
    active = true,
    customFilter = undefined,
) {
    let cssPadding = false;
    if (_.isString(disablePadding)) {
        if (disablePadding === 'custom') {
            disablePadding = true;
            cssPadding = true;
        } else {
            disablePadding = false;
        }
    }
    return {
        id,
        description,
        order,
        field,
        columnid: type,
        headerType,
        size,
        align,
        disablePadding,
        cssPadding,
        enableFilter,
        width,
        minWidth,
        option,
        active,
        customFilter,
    };
}

export const AgyleTable = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [sortOrder, setSortOrder] = useState([]);
    const [filters, setFilters] = useState([]);
    const [openFilters, setOpenFilters] = useState(false);
    const columnsOrdered = _.orderBy(props.columns, ['order']);

    // Columns
    const agylePreference = useSelector(agylePref);
    const [isOptionOpen, openOption, closeOption] = useOpenState(false, false);
    const [columns, setColumns] = useState([]);

    useEffect(() => {
        let init = _.filter(columnsOrdered, (col) => col.active);
        if (!_.isEmpty(props.customizableColumnsPath)) {
            if (agylePreference?.[props.customizableColumnsPath] && !_.isEmpty(agylePreference?.[props.customizableColumnsPath])) {
                const cloneProperties = _.cloneDeep(agylePreference[props.customizableColumnsPath]);
                const loadData = [];

                _.forEach(init, (col) => {
                    let find = _.find(cloneProperties, { id: col.id });
                    if (col.option?.includes('forceShow')) find = find ? find : col;

                    loadData.push({
                        ...col,
                        order: find ? find.order : col.order,
                    });
                });
                init = [...loadData];
            }
        }
        setColumns(_.sortBy(init, ['order']));
    }, [props.columns, agylePreference, props.customizableColumnsPath]);

    useEffect(() => {
        setOpenFilters(props.showFilters);
        if (!props.showFilters) {
            setFilters([]);
        }
    }, [props.showFilters]);

    const handleSort = (obj) => {
        let clone = _.cloneDeep(sortOrder);
        const index = _.findIndex(clone, { id: obj.id });

        if (index !== -1) {
            if (clone[index].direction === 'desc') {
                clone.splice(index, 1);
            } else {
                clone[index].direction = 'desc';
            }
        } else {
            clone.unshift({ id: obj.id, direction: 'asc', columnid: obj.columnid, field: obj.field });
        }
        setSortOrder(clone);
    };

    const clearSort = () => {
        setSortOrder([]);
    };

    const clearFilters = () => {
        setFilters([]);
    };

    const toggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    // Restore Default Layout
    const handleDefaultColumns = () => {
        setColumns(columnsOrdered);
        dispatch(saveAgylePreference({ [props.customizableColumnsPath]: null }));
        closeOption();
        dispatch(setSnackbar({ columnid: 'success', message: t('Load default columns layout.') }));
    };

    // Save columns configuration
    const handleChangeColumns = (activeColumns, disableColumns) => {
        const disabled = [];
        const active = [];
        for (let i = 0; i < activeColumns.length; i++) {
            active.push({
                ...activeColumns[i],
                order: i + 1,
            });
        }

        _.forEach(disableColumns, (col) => {
            disabled.push({
                ...col,
                order: null,
            });
        });
        const newList = [...active, ...disabled];

        setColumns([...newList]);

        const userPref = [];
        _.forEach(newList, (column) => {
            userPref.push({
                id: column.id,
                order: column.order,
            });
        });
        dispatch(setSnackbar({ columnid: 'success', message: t('Columns order saved correctly.') }));
        dispatch(saveAgylePreference({ [props.customizableColumnsPath]: [...userPref] }));
        closeOption();
    };

    const getColumnsWithOrder = () => {
        const cols = _.filter(columns, (col) => !_.isNull(col.order));
        return cols || [];
    };

    const renderPagination = () => {
        const pagination = props.pagination;
        if (!pagination || _.isEmpty(props.data)) return null;

        const page = pagination.page || 1;
        const size = pagination.size || props.data.length;
        const total = pagination.total || props.data.length;
        const pageCount = pagination.pageCount || Math.ceil(total / size);
        const currentRecords = Math.min((page - 1) * size + props.data.length, total);

        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 10 }}>
                <div>
                    {t('Records')}: {currentRecords} / {total}
                </div>

                <Pagination page={page} onChange={(_e, nextPage) => pagination.onChange(nextPage)} count={pageCount} shape="rounded" />
            </div>
        );
    };

    return (
        <>
            <Grid container direction={'row'}>
                {!_.isEmpty(props.customizableColumnsPath) && (
                    <>
                        <Tooltip title={'Toggle columns order'}>
                            <IconButton size="small" disableRipple={true} onClick={openOption}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" variant="middle" flexItem style={{ margin: '0 10px' }} />
                    </>
                )}
                {!props.disableFilter && (
                    <>
                        <Tooltip title={'Toggle filters'}>
                            <IconButton size={'small'} disableRipple={true} onClick={toggleFilters}>
                                <FilterAltIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={'Clear all filters'}>
                            <IconButton size={'small'} disableRipple={true} onClick={clearFilters}>
                                <FilterAltOffIcon />
                            </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" variant="middle" flexItem style={{ margin: '0 10px' }} />
                    </>
                )}
                {sortOrder.length > 0 && (
                    <Tooltip title={'Clear all sorts'}>
                        <IconButton size={'small'} disableRipple={true} onClick={clearSort}>
                            <ExploreOffIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Grid>

            <TableContainer
                className={clsx(
                    getTableScrollbar(),
                    classes.tableContainer,
                    !props.noMaxHeight && 'maxHeight',
                    props.edRefresh && 'refresh',
                    props.tableContainerClass,
                )}
            >
                <Table stickyHeader={props.stickyHeader}>
                    <TableHeaders
                        sortOrder={sortOrder}
                        columns={getColumnsWithOrder()}
                        sort={handleSort}
                        openFilters={openFilters}
                        filters={filters}
                        setFilters={setFilters}
                        props={props}
                        edRefresh={props.edRefresh}
                    />
                    {!_.isEmpty(props.data) && (
                        <TableRows
                            data={props.data}
                            columns={getColumnsWithOrder()}
                            editItem={props.editItem}
                            disabledAction={props.disabledAction}
                            deleteItem={props.deleteItem}
                            customActionItem={props.customActionItem}
                            onClickFunc={props.onClick}
                            sortOrder={sortOrder}
                            filters={filters}
                            edRefresh={props.edRefresh}
                            minHeightRowCell={props.minHeightRowCell}
                            props={props}
                            cancelFiled={props.cancelFiled}
                            highlightRow={props.highlightRow}
                        />
                    )}
                </Table>
            </TableContainer>

            {_.isEmpty(props.data) && <InformationBox type={'info'}>{t('No records to show.')}</InformationBox>}

            {renderPagination()}

            <Dialog open={isOptionOpen} maxWidth="md" fullWidth={true} onClose={closeOption}>
                <PriorityOptions
                    onClose={closeOption}
                    columns={columns}
                    saveColumns={handleChangeColumns}
                    default={handleDefaultColumns}
                    title={t('Bed Booking Columns')}
                    idField="id"
                />
            </Dialog>
        </>
    );
};

AgyleTable.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    stickyHeader: PropTypes.bool,
    disableFilter: PropTypes.bool,
    editItem: PropTypes.func,
    deleteItem: PropTypes.func,
    disabledAction: PropTypes.bool,
    onClick: PropTypes.func,
    customizableColumnsPath: PropTypes.string,
    customActionItem: PropTypes.any,
    edRefresh: PropTypes.bool,
    minHeightRowCell: PropTypes.number,
    cancelFiled: PropTypes.string,
    highlightRow: PropTypes.object,
    pagination: PropTypes.object,
};

AgyleTable.defaultProps = {
    columns: [],
    data: [],
    stickyHeader: true,
    disabledAction: false,
    disableFilter: false,
    customizableColumnsPath: '',
    edRefresh: true,
};

const TableHeaders = ({ sortOrder, sort, columns, openFilters, filters, setFilters, edRefresh, props }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [currentFilterValue, setCurrentFilterValue] = useState(null);
    const [currentFilterType, setCurrentFilterType] = useState(null);
    const [filterPopover, setFilterPopover] = React.useState({ open: false, column: null, type: 'default' });
    const anchorEl = React.useRef(null);
    const openFilter = (column) => {
        const filterType = column.customFilter?.type || 'default';
        setCurrentFilterValue(findValue('filter', filterType, column));
        setCurrentFilterType(findValue('filterType', filterType, column));
        setFilterPopover({ open: true, column: column, type: filterType });
    };
    const findSortDirection = (id, notBoolean) => {
        if (sortOrder) {
            const find = _.find(sortOrder, { id });

            if (notBoolean) {
                return find ? find?.direction : 'asc';
            }
            return find ? find?.direction : false;
        }
        return notBoolean ? 'asc' : false;
    };

    const headerCellClass = () => {
        if (props.headerCellClass) return props.headerCellClass;

        return clsx(classes.headCell, edRefresh && 'refresh');
    };

    const isColumnFiltered = (column) => {
        const index = _.findIndex(filters, { name: column.id });
        const oldValue = index !== -1 ? filters[index] : null;
        return !!oldValue?.filter;
    };

    const renderCell = (col, index) => {
        switch (col.headerType) {
            case 'empty':
                return (
                    <TableCell
                        key={col.id || index}
                        size={col.size || 'small'}
                        align={col.align || 'center'}
                        padding={col.disablePadding ? 'none' : 'normal'}
                        className={headerCellClass()}
                        style={{ width: col.width || 'auto', minWidth: col.minWidth || '' }}
                    />
                );

            case 'notSort':
                return (
                    <TableCell
                        key={col.id || index}
                        size={col.size || 'small'}
                        align={col.align || 'center'}
                        padding={col.disablePadding ? 'none' : 'normal'}
                        className={headerCellClass()}
                        style={{ width: col.width || 'auto', minWidth: col.minWidth || '' }}
                        onClick={(event) => (anchorEl.current = event.currentTarget)}
                    >
                        {openFilters && col.enableFilter && (
                            <Badge
                                variant="dot"
                                invisible={!isColumnFiltered(col)}
                                overlap="circular"
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                sx={{
                                    '& .MuiBadge-badge': {
                                        backgroundColor: '#4caf50',
                                        color: '#4caf50',
                                        top: 6,
                                        right: 6,
                                        minWidth: 8,
                                        height: 8,
                                    },
                                }}
                            >
                                <IconButton size="small" disableRipple={true} onClick={() => openFilter(col)}>
                                    <FilterListIcon fontSize={edRefresh ? 'small' : 'medium'} />
                                </IconButton>
                            </Badge>
                        )}
                        {col.description}
                    </TableCell>
                );

            default:
                return (
                    <TableCell
                        key={col.id || index}
                        size={col.size || 'small'}
                        align={col.align || 'center'}
                        padding={col.disablePadding ? 'none' : 'normal'}
                        className={headerCellClass()}
                        style={{ width: col.width || 'fit-content', minWidth: col.minWidth || '' }}
                        sortDirection={findSortDirection(col.id)}
                        onClick={(event) => (anchorEl.current = event.currentTarget)}
                    >
                        <>
                            {openFilters && col.enableFilter && (
                                <Badge
                                    variant="dot"
                                    invisible={!isColumnFiltered(col)}
                                    overlap="circular"
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: '#4caf50',
                                            color: '#4caf50',
                                            top: 6,
                                            right: 6,
                                            minWidth: 8,
                                            height: 8,
                                        },
                                    }}
                                >
                                    <IconButton size="small" disableRipple={true} onClick={() => openFilter(col)}>
                                        <FilterListIcon fontSize={edRefresh ? 'small' : 'medium'} />
                                    </IconButton>
                                </Badge>
                            )}
                            <TableSortLabel
                                active={Boolean(findSortDirection(col.id))}
                                direction={findSortDirection(col.id, true)}
                                onClick={sort.bind(this, col)}
                            >
                                {col.description}
                            </TableSortLabel>
                        </>
                    </TableCell>
                );
        }
    };

    const findValue = (type, filterPopoverType, column) => {
        const filterDefault = filterPopoverType === 'timerange' ? { from: null, to: null } : type === 'filterType' ? 'equals' : '';
        if (!column) return filterDefault;
        const find = _.find(filters, { name: column.id });
        if (find) return find[type] ? find[type] : filterDefault;
        return filterDefault;
    };

    const filterValue = (data, olddata, index, type, column) => {
        const oldValue = index !== -1 ? olddata[index] : null;
        if (column.customFilter?.type === 'timerange') {
            if (oldValue?.filter && _.isObject(oldValue?.filter)) {
                return { ...oldValue.filter, [data.type]: data.date };
            }
            return { [data.type]: data.date };
        }
        return data;
    };

    const filterHandler = (event, type) => {
        let clone = _.cloneDeep(filters);
        const index = _.findIndex(clone, { name: filterPopover.column.id });
        const value = filterValue(event, clone, index, type, filterPopover.column);
        if (index !== -1) {
            if (type === 'filter') clone[index].filter = value;
            else clone[index].filterType = value;
        } else {
            clone.push({
                name: filterPopover.column.id,
                filter: type === 'filter' ? value : '',
                filterType: type !== 'filter' ? value : 'equals',
            });
        }
        if (type === 'filter') {
            setCurrentFilterValue(value);
        } else {
            setCurrentFilterType(value);
        }
        setFilters([...clone]);
    };

    const handleClosePopover = () => {
        setFilterPopover({ open: false, column: null });
    };

    const handleEnter = (e) => {
        if (e.code === 'Enter') {
            e.preventDefault();
            handleClosePopover();
        }
    };

    return (
        <Fragment>
            <TableHead>
                <TableRow className={props.headerClass}>{columns.map((col, index) => renderCell(col, index + 'cell'))}</TableRow>
            </TableHead>

            <Popover
                open={filterPopover.open}
                anchorEl={anchorEl.current}
                onClose={handleClosePopover}
                onKeyDown={handleEnter}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                {filterPopover.type === 'default' && (
                    <>
                        <Grid item xs={12} style={{ margin: '8px 8px 0px 8px' }}>
                            <ToggleButtonGroup
                                size={'small'}
                                value={currentFilterType}
                                exclusive
                                fullWidth={true}
                                style={{ height: 25 }}
                                onChange={(event) => filterHandler(event, 'filterType')}
                                color="tertiary"
                            >
                                <ToggleButton value="equals">{t('Equals')}</ToggleButton>
                                <ToggleButton value="includes">{t('Includes')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                size={'small'}
                                name="filter"
                                label={t('Filter')}
                                className={classes.input}
                                onChange={(event) => filterHandler(event.currentTarget.value, 'filter')}
                                value={currentFilterValue}
                                color="tertiary"
                            />
                        </Grid>
                    </>
                )}
                {filterPopover.type === 'select' && (
                    <>
                        <Grid item xs={12} sx={{ m: 1, minWidth: 250 }}>
                            <AgyleSelectField
                                label={t('Select filter value')}
                                onChange={(value) => filterHandler(value, 'filter')}
                                options={filterPopover?.column?.customFilter?.options}
                                value={currentFilterValue}
                                noBorder={false}
                                withNewBackground
                                fullWidth
                                size="small"
                                clearButtonVisible={true}
                            />
                        </Grid>
                    </>
                )}
                {filterPopover.type === 'timerange' && (
                    <>
                        <Grid item xs={12} sx={{ m: 1 }}>
                            <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <AgyleDatePicker
                                        type="dateTime"
                                        inputFormat={config.defaultDateTimeFormat}
                                        label={t('Date and Time From')}
                                        value={currentFilterValue?.from}
                                        onChange={(event) => {
                                            filterHandler({ date: event, type: 'from' }, 'filter');
                                        }}
                                        size="small"
                                        noBorder={false}
                                        withNewBackground
                                    />
                                    <Tooltip title={t('Clear')}>
                                        <IconButton
                                            size="small"
                                            aria-label={t('Clear from date')}
                                            onClick={() => {
                                                filterHandler({ date: null, type: 'from' }, 'filter');
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <AgyleDatePicker
                                        type="dateTime"
                                        inputFormat={config.defaultDateTimeFormat}
                                        label={t('Date and Time To')}
                                        value={currentFilterValue?.to}
                                        onChange={(event) => {
                                            filterHandler({ date: event, type: 'to' }, 'filter');
                                        }}
                                        size="small"
                                        noBorder={false}
                                        withNewBackground
                                    />
                                    <Tooltip title={t('Clear')}>
                                        <IconButton
                                            size="small"
                                            aria-label={t('Clear to date')}
                                            onClick={() => {
                                                filterHandler({ date: null, type: 'to' }, 'filter');
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Grid>
                    </>
                )}
            </Popover>
        </Fragment>
    );
};

const TableRows = ({
    data,
    columns,
    editItem,
    disabledAction,
    deleteItem,
    onClickFunc,
    sortOrder,
    filters,
    customActionItem,
    edRefresh,
    minHeightRowCell,
    highlightRow,
    props,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();

    // Dictionaries
    const dictReferredToService = useSelector(referredToService);
    const [specialties] = useDict('Specialties', '', 'relevent eq true');
    const [trudWardList] = useWardDict();

    const [tableData, setTableData] = useState(data);

    useEffect(() => {
        let clone = _.cloneDeep(data);
        if (!_.isEmpty(data)) {
            _.forEach(sortOrder, (order) => {
                clone = _.orderBy(
                    clone,
                    (row) => {
                        return sortByType(order.columnid, order.field, row);
                    },
                    [order.direction],
                );
            });
            setTableData(clone);
        }
    }, [sortOrder, data]);

    // Special sort method
    const sortByType = (fieldType, field, patient) => {
        const val = _.get(patient, field);
        switch (fieldType) {
            case 'bedBookingButton':
                return val?.value || null;
            case 'timeBedWait':
                return moment(val?.requested);

            default:
                return val;
        }
    };

    const renderActionButton = (data) => {
        return (
            <div style={{ display: customActionItem ? 'flex' : '', justifyContent: 'flex-end', alignItems: 'center' }}>
                {editItem && (
                    <IconButton size={'small'} onClick={editItem.bind(this, data)} disabled={disabledAction}>
                        <EditIcon />
                    </IconButton>
                )}

                {deleteItem && (
                    <IconButton size={'small'} onClick={deleteItem.bind(this, data)} disabled={disabledAction}>
                        <DeleteIcon />
                    </IconButton>
                )}
                {customActionItem && customActionItem(data)}
            </div>
        );
    };

    const getNoteName = (noteType) => {
        if (noteType) {
            let text = noteType;
            if (noteType.includes('.')) {
                const arr = noteType.split('.');
                text = _.last(arr);
            }

            const exception = formNameException.get(text);
            if (exception) text = exception;

            return _.startCase(text);
        }
        return '-';
    };

    const printArray = (array) => {
        let text = '';

        _.forEach(array, (obj, index) => {
            text += index === 0 ? _.startCase(obj) : `, ${_.startCase(obj)}`;
        });

        return text !== '' ? text : '-';
    };

    const getAreaTypeMap = (value) => {
        const MAP = new Map([
            ['list', 'Waiting List'],
            ['area', 'Zone'],
        ]);
        const findVal = MAP.get(value);
        return findVal || _.startCase(value);
    };

    const getObs = (score, renderEmpty) => {
        if (_.isNil(score) || score < 0) return renderEmpty ? '' : '-';

        let riskLev;
        if (score >= 0 && score < 5) {
            riskLev = riskLevels.low;
        }
        if (score >= 5 && score < 7) {
            riskLev = riskLevels.mediumLow;
        }
        if (score >= 7 && score < 9) {
            riskLev = riskLevels.medium;
        }
        if (score >= 9) riskLev = riskLevels.high;

        const color = riskLev && classes[`obsColor${riskLev[0].toUpperCase() + riskLev.slice(1)}`];

        return <span className={clsx(classes.scoreColor, color)}>{score}</span>;
    };

    const getCountDown = (value) => {
        if (!moment(value).isValid()) {
            return <span>{t('Times up!')}</span>;
        }
        return (
            <div
                style={
                    value.isBefore(moment())
                        ? {
                              color: 'red',
                              fontWeight: 600,
                          }
                        : value.diff(moment(), 'minutes') < 15
                          ? {
                                color: 'coral',
                                fontWeight: 600,
                            }
                          : {}
                }
            >
                <TimerCountUp time={value} limit={0} upperLimit={1} limitMessage={t('Times up!')} countDown />
            </div>
        );
    };

    const getObsTime = (value, withTime, isPSC, data) => {
        if (!value) {
            return isPSC && data?.name !== '' ? 'Not Started' : '-';
        } else if (value === 'cancelled') {
            return <span className={classes.cancelledObs}>Cancelled</span>;
        } else if (value === 'suspended') {
            return <span className={classes.suspendedObs}>Suspended</span>;
        } else if (moment(value).isValid()) {
            return (
                <>
                    {withTime && <span>({moment(value).format(config.defaultTimeFormat)})</span>}

                    <div
                        style={
                            moment(value).isBefore(moment())
                                ? {
                                      color: 'red',
                                      fontWeight: 600,
                                  }
                                : moment(value).diff(moment(), 'minutes') < 15
                                  ? {
                                        color: 'coral',
                                        fontWeight: 600,
                                    }
                                  : {}
                        }
                    >
                        <TimerCountUp time={value} limit={0} upperLimit={1} limitMessage={`${isPSC ? 'PSC' : 'Obs'} Overdue`} countDown />
                    </div>
                </>
            );
        }
        return value;
    };

    const getPlans = (value) => {
        if (value && _.isArray(value) && !_.isEmpty(value)) {
            return (
                <div>
                    {value.map((plan, index) => (
                        <div key={plan.id}>
                            {`${index + 1}. ${plan.text}`} {plan.checked ? <CheckIcon color={'success'} /> : ''}
                        </div>
                    ))}
                </div>
            );
        }
        return '-';
    };

    const getBedBookingButton = (val, options) => {
        const bedStatus = options?.includes('bedStatus');
        const conclusionStatus = options?.includes('conclusionStatus');
        const siteStatus = options?.includes('siteStatus');

        const cc = conclusionStatus && val?.value !== 'No Clinical Conclusion';
        if ((!_.isEmpty(val), _.isObject(val))) {
            if (_.isEmpty(val?.value)) {
                return (
                    <div className={classes.centerRow}>
                        <AddCircleOutlineOutlinedIcon style={{ fontSize: '1.2rem' }} />
                    </div>
                );
            }
            return (
                <div
                    className={clsx(
                        bedStatus && classes.bedStatus,
                        val?.value,
                        siteStatus && classes.siteStatus,
                        val?.value,
                        cc && classes.conclusionStatus,
                        _.snakeCase(val?.value),
                        !bedStatus && !cc && classes.centerRow,
                    )}
                >
                    <div style={{ width: '100%' }}>{val?.value}</div>
                </div>
            );
        }

        return '-';
    };

    const getBackgroundField = (val, options) => {
        const fontColor = options?.color || '#000000';
        const backgroundColor = options?.backgroundColor || '#EEEEE4';

        return (
            <div
                className={classes.backgroundButton}
                style={{
                    color: fontColor,
                    backgroundColor: backgroundColor,
                }}
            >
                <div style={{ width: '100%' }}>{(options?.customVal ?? val) || '-'}</div>
            </div>
        );
    };

    const getTimeBedWait = (bedWaitObj) => {
        return (
            <div className={classes.timer}>
                <TimerCountUpSimple time={bedWaitObj?.requested} renderWithBG limit={240} upperLimit={-1} limitMessage={'>8h'} />
            </div>
        );
    };

    const checkForAction = (data, column) => {
        const value = _.get(data, column.field);
        switch (column.columnid) {
            case 'bedBookingButton':
                return value?.onClick;
            default:
                return null;
        }
    };

    const getReferrals = (data) => {
        if (!_.isEmpty(data)) {
            const referrals = [];

            _.forEach(data, (referral) => {
                const find = _.find(dictReferredToService, { __id: referral?.ReferredToService_SnomedCt?.__id });
                if (find)
                    referrals.push({
                        id: find.notes,
                        group: find.group,
                        description: find.__description,
                        snomedCode: find.snomedCode,
                        completed: referral.ReferredToServiceAssessmentTime !== '',
                    });
            });

            return (
                <>
                    {referrals.map((ref, index) => (
                        <Tooltip key={index} title={ref.description}>
                            <div
                                style={{ backgroundColor: getReferralsColor(ref) }}
                                className={clsx(classes.referral, ref.completed && 'completed')}
                            >
                                {ref.id}
                            </div>
                        </Tooltip>
                    ))}
                </>
            );
        }
        return '-';
    };

    const getSpecialties = (data) => {
        if (!_.isEmpty(data)) {
            const specArr = [];

            _.forEach(data, (spec) => {
                const find = _.find(specialties, { __id: spec });
                if (find) specArr.push(find);
            });
            return _.join(
                specArr.map((s) => _.startCase(s?.__description?.toLowerCase())),
                ', ',
            );
        }
        return '-';
    };
    const getClinicianOption = (status) => {
        switch (status) {
            case 'setLogged':
                return 'Set logged';
            case 'setBlank':
                return 'Set blank';
            case 'setClinician':
                return 'Set clinician';
            default:
                return '-';
        }
    };

    const getClinics = (data) => {
        if (!_.isEmpty(data)) {
            const wardArr = [];

            _.forEach(data, (ward) => {
                const find = _.find(trudWardList, { __id: ward });
                if (find) wardArr.push(find);
            });
            return _.join(
                wardArr.map((s) => _.startCase(s?.wardName?.toLowerCase())),
                ', ',
            );
        }
        return '-';
    };

    const getEscalationLevel = (value) => {
        return <div className={clsx(classes.escalationLevel, value)}>{_.capitalize(value)}</div>;
    };

    const getCleaningLevel = (value) => {
        return (
            <div>
                <div className={clsx(classes.cleaningLevel, `level${value}`)} style={{ textAlign: 'center' }}>
                    {value || '-'}
                </div>
            </div>
        );
    };

    const getAcknowledgeCleaning = (value) => {
        if (value?.checked) {
            return (
                <>
                    <div>{value?.user}</div>

                    <div>{moment(value?.when).format(config.defaultDateTimeFormat)}</div>
                </>
            );
        } else {
            return t('N/A');
        }
    };

    const getActionCleaning = (data) => {
        let status = data?.toLowerCase();
        return <div className={clsx(classes.status, status)}>{_.capitalize(status) || 'status'}</div>;
    };
    const getDateFormat = (column) => {
        if (column.option && _.isArray(column.option) && column.option.length > 0 && _.isString(column.option[0])) {
            return column.option[0];
        }
        return config.defaultDateTimeFormat;
    };

    const getCustomFiledValue = (data, column) => {
        if (column.field === '$custom') {
            if (column.option && _.isArray(column.option) && column.option.length > 0 && _.isFunction(column.option[0])) {
                return column.option[0](data);
            }
        }
        return _.get(data, column.field);
    };

    const getEDValue = (wardId) => {
        let label = 'Other';
        let chipColor = theme.palette.attendanceType.other;
        let showBorder = false;
        if (wardId) {
            if (wardId?.includes('ED_')) {
                label = 'ED';
                chipColor = theme.palette.attendanceType.ed;
            } else if (wardId?.includes('UTC_')) {
                label = 'UTC';
                chipColor = theme.palette.attendanceType.utc;
                showBorder = true;
            } else if (wardId?.includes('SDEC_')) {
                label = 'SDEC';
                chipColor = theme.palette.attendanceType.sdec;
            }
        }
        return { customVal: label, backgroundColor: chipColor, showBorder };
    };

    const renderRow = (data, index, column, valueOpt) => {
        const value = valueOpt || getCustomFiledValue(data, column);

        // Add new type here
        switch (column.columnid) {
            case 'count':
                return index + 1;
            case 'startCase':
                return value ? _.startCase(value) : '-';
            case 'clinicalNotesNames':
                return getNoteName(value);
            case 'dict':
                switch (column.id) {
                    case 'dischargeFollowUp':
                    case 'dischargeDestination':
                    case 'dischargeStatus':
                        return value?.snomedPreferredTerm || value?.__description;
                    case 'diag':
                        return value && value?.diagnosisMax ? value?.snomedFullySpecifiedName : value?.__description;
                    default:
                        return value ? value?.__description : '-';
                }
            case 'arrayDict':
                let text = '';
                if (column.id === 'diag' && value) {
                    if (value?.diagnosisMax) {
                        text = value?.map((v) => v?.snomedFullySpecifiedName).join(', ');
                    } else {
                        text = value?.map((v) => v?.__description).join(', ');
                    }
                } else if (_.isArray(value) && !_.isEmpty(value)) {
                    text = value.map((v) => v?.__description).join(', ');
                } else {
                    text = '-';
                }
                return text;
            case 'array':
                return printArray(value);
            case 'arrayLength':
                return _.isArray(value) ? value.length : 0;
            case 'boolean':
                return value ? 'True' : 'False';
            case 'booleanCheckbox':
                return <AgyleCheckBox disabled onChange={() => null} value={value} />;
            case 'action':
                return renderActionButton(data);
            case 'areaType':
                return getAreaTypeMap(value);
            case 'obs':
            case 'obsEmpty':
                return getObs(value, column.columnid === 'obsEmpty');
            case 'obsTime':
            case 'obsTimeExtended':
            case 'pscTime':
                return getObsTime(value, column.columnid !== 'obsTime', column.columnid === 'pscTime', data);
            case 'plans':
                return getPlans(value);
            case 'date':
                if (moment(value).isValid()) {
                    return moment(value).format(getDateFormat(column));
                }
                return '';
            case 'dateECDS': {
                if (!_.isEmpty(column.option) && _.isArray(column.option)) {
                    const getLocalDateText = (type) => {
                        const date = _.get(data, column.option[0]);
                        const time = _.get(data, column.option[1]);
                        const local = UtilsMomentJS.getLocalDateFromUTCECDSFormat(date, time);

                        if (local) {
                            return type === 'date' ? local.format(config.defaultDateFormat) : local.format(config.defaultTimeFormat);
                        }
                        return '-';
                    };
                    return getLocalDateText(column.option[2]);
                }
                return '';
            }
            case 'dateDefault':
                if (moment(value).isValid()) return moment(value).format(config.defaultDateTimeFormat);
                return '';
            case '$custom':
                if (column.option && _.isArray(column.option) && column.option.length > 0 && _.isFunction(column.option[0])) {
                    if (!data && valueOpt) return valueOpt;
                    return column.option[0](column, data);
                }
                return '';
            case 'bedBookingButton':
                return getBedBookingButton(value, column?.option);
            case 'backgroundField':
                return getBackgroundField(value, column?.option);
            case 'bbType': {
                let opt = null;
                if (value !== 'Non ED') {
                    opt = getEDValue(value);
                }

                return getBackgroundField(value, opt);
            }
            case 'timeBedWait':
                if (!value?.requested) return '';
                return getTimeBedWait(value);
            case 'countDown':
                return getCountDown(value);
            case 'time':
            case 'timeEmpty':
                if (!value) return column.columnid === 'timeEmpty' ? '' : '-';
                return (
                    <div className={classes.timer}>
                        <TimerCountUpSimple time={value} renderWithBG limit={240} upperLimit={-1} limitMessage={'>8h'} />
                    </div>
                );
            case 'copyNumber': {
                const handleCopyPtNumber = (number) => {
                    if (!navigator.clipboard || !number) return;
                    navigator.clipboard.writeText(number).then(() => {
                        dispatch(
                            setSnackbar({
                                columnid: 'success',
                                message: 'The patient number has been copied to the clipboard.',
                            }),
                        );
                    });
                };

                return (
                    <>
                        <Tooltip title={t('Copy Patient Number')}>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyPtNumber(value);
                                }}
                                size="small"
                            >
                                <CopyIcon />
                            </IconButton>
                        </Tooltip>

                        {value}
                    </>
                );
            }
            case 'fromDict': {
                const optDict = column.option[0];
                if (!optDict) return '-';
                const findDict = _.find(optDict?.dict, { [optDict?.searchField]: value });
                return findDict?.[optDict?.displayField] || '-';
            }
            case 'referrals':
                return getReferrals(value);
            case 'clinicianOption':
                return getClinicianOption(value);
            case 'specialties':
                return getSpecialties(value);
            case 'clinics':
                return getClinics(value);
            case 'escalationLevel':
                return getEscalationLevel(value);
            case 'cleaningLevel':
                return getCleaningLevel(value);
            case 'acknowledgeCleaning':
                return getAcknowledgeCleaning(value);
            case 'statusCleaning':
                return getActionCleaning(value);
            case 'advanceDescription':
                if (value?.diagnosisMax) return value?.snomedFullySpecifiedName || '-';
                return value?.snomedDescription || '-';
            case 'advanceSnomedCode':
                if (value?.diagnosisMax) return value?.usedParentSnomedCode || '-';
                return value?.snomedCode || '-';
            case 'transformCustomData':
                if (column?.option?.transformVal) {
                    return column.option.transformVal(data);
                }
                return '-';
            case 'acknowledge':
                return getAcknowledgedStatus(value);
            default:
                return value || '-';
        }
    };

    const getAcknowledgedStatus = (referral) => {
        const isAck = referral?.acknowledge;

        return (
            <div className={classes.divRow} style={{ marginLeft: -14 }}>
                <AgyleButton
                    disabled
                    color="primary"
                    variant="outlined"
                    startIcon={<CheckCircleOutlineIcon />}
                    className={clsx(classes.button, 'acknowledge', isAck && 'done')}
                    style={isAck ? { color: '#4eac6f' } : {}}
                >
                    {t(isAck ? 'Acknowledged' : 'Not acknowledged')}
                </AgyleButton>
            </div>
        );
    };

    const handleClick = (item, e) => {
        if (onClickFunc) onClickFunc(item, e);
    };

    const checkFilterValue = (value, col, type, filter) => {
        if (col.customFilter?.type === 'timerange') {
            if (filter.from && moment(value).isBefore(moment(filter.from))) return false;
            if (filter.to && moment(value).isAfter(moment(filter.to))) return false;
            return true;
        }
        const valueRender = renderRow(null, null, col, value);
        if (type === 'equals') return _.toLower(valueRender) === _.toLower(filter);
        else return _.toLower(valueRender).includes(_.toLower(filter));
    };

    const showData = (data = []) => {
        let sortedAndFilteredArray = data;
        const notEmptyFilters = _.filter(filters, (f) => f.filter !== '');

        if (!_.isEmpty(sortOrder)) {
            sortedAndFilteredArray = _.orderBy(
                sortedAndFilteredArray,
                _.reverse(sortOrder.map((e) => e.field)),
                _.reverse(sortOrder.map((e) => e.direction)),
            );
        }

        if (!_.isEmpty(notEmptyFilters)) {
            const newRows = [];
            _.forEach(sortedAndFilteredArray, (data) => {
                let check = true;

                _.forEach(notEmptyFilters, (filter) => {
                    if (!check) return;
                    const colFiled = _.find(columns, { id: filter.name });
                    if (colFiled) {
                        const value = _.get(data, colFiled?.field);
                        check = checkFilterValue(value, colFiled, filter.filterType, filter.filter);
                    }
                });

                if (check) newRows.push(data);
            });
            sortedAndFilteredArray = newRows;
        }
        return sortedAndFilteredArray;
    };

    const cellClass = (col, index) => {
        if (_.isUndefined(col.className)) {
            if (col.option && col.option.length > 0) {
                _.forEach(col.option, (opt) => {
                    if (_.isObject(opt) && opt.className) {
                        col.className = clsx(
                            col?.option?.includes('sticky') && classes.stickyCol,
                            index % 2 && 'odd',
                            opt.className,
                            edRefresh && classes.refreshCol,
                        );
                    }
                });
                if (!col.className) {
                    col.className = clsx(
                        col?.option?.includes('sticky') && classes.stickyCol,
                        index % 2 && 'odd',
                        edRefresh && classes.refreshCol,
                    );
                }
            }
        }
        return clsx(col.className, edRefresh && classes.refreshCol);
    };

    const rowClass = (index) => {
        if (props.rowClass) {
            return props.rowClass;
        }
        return clsx(classes.tableRow, index % 2 && 'odd');
    };

    return (
        <TableBody>
            {showData(tableData).map((rowData, index) => (
                <TableRow key={rowData?.__id || Math.random()} className={rowClass(index)} onClick={(e) => handleClick(rowData, e)}>
                    {columns?.map((col, indexCol) => {
                        const action = checkForAction(rowData, col);
                        return (
                            <TableCell
                                key={col.id || indexCol}
                                size={col.size || 'small'}
                                align={col.align || 'center'}
                                style={{
                                    width: col.width || 'fit-content',
                                    padding: col.cssPadding ? 'undefined' : 2,
                                    height: minHeightRowCell || 'auto',
                                }}
                                padding={col.disablePadding ? 'none' : 'normal'}
                                onClick={action}
                                className={clsx(
                                    cellClass(col, index),
                                    props.cancelFiled && rowData?.[props.cancelFiled] && classes.strikeThrough,
                                    _.isEqual(rowData, highlightRow) && classes.highlightRow,
                                )}
                            >
                                {renderRow(rowData, index, col, null)}
                            </TableCell>
                        );
                    })}
                </TableRow>
            ))}
        </TableBody>
    );
};
